import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Trash2, Plus, MapPin, Package, Phone } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const SIZES = [
  { id: 'small', label: 'Small', mult: 1.0 },
  { id: 'medium', label: 'Medium', mult: 1.5 },
  { id: 'large', label: 'Large', mult: 2.0 },
];

const blankStop = () => ({
  receiverName: '', receiverPhone: '', location: '',
  packageDescription: '', packageSize: 'small', codAmount: 0, notes: '',
});

const BookMultiDrop = () => {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState('');
  const [pickupContact, setPickupContact] = useState('');
  const [stops, setStops] = useState([blankStop()]);
  const [submitting, setSubmitting] = useState(false);

  const addStop = () => setStops([...stops, blankStop()]);
  const removeStop = (idx) => setStops(stops.filter((_, i) => i !== idx));
  const updateStop = (idx, field, value) => {
    const next = [...stops];
    next[idx] = { ...next[idx], [field]: value };
    setStops(next);
  };

  const estimate = (() => {
    const base = 20;
    const perDrop = 15;
    const factor = Math.max(...stops.map(s => SIZES.find(sz => sz.id === s.packageSize)?.mult || 1));
    return ((base + perDrop * stops.length) * factor).toFixed(2);
  })();

  const codTotal = stops.reduce((sum, s) => sum + (parseFloat(s.codAmount) || 0), 0).toFixed(2);

  const handleSubmit = async () => {
    if (!pickup.trim()) { toast({ title: 'Pickup is required', variant: 'destructive' }); return; }
    if (stops.some(s => !s.receiverName.trim() || !s.receiverPhone.trim() || !s.location.trim())) {
      toast({ title: 'Fill receiver name, phone, and location for each stop', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const api = (await import('../utils/api')).default;
      const payload = {
        pickupLocation: pickup.trim(),
        pickupContact: pickupContact.trim() || null,
        stops: stops.map(s => ({
          receiverName: s.receiverName.trim(),
          receiverPhone: s.receiverPhone.trim(),
          location: s.location.trim(),
          packageDescription: s.packageDescription.trim() || null,
          packageSize: s.packageSize,
          codAmount: parseFloat(s.codAmount) || 0,
          notes: s.notes.trim() || null,
        })),
        paymentMethod: 'cash',
      };
      const { data } = await api.post('/deliveries/book-multi-drop', payload);
      toast({ title: 'Booking created', description: `${data.totalStops} stops · K${data.estimatedFare} total` });
      const trackUrl = `/track/package/${data.trackingToken}`;
      navigator.clipboard?.writeText(`${window.location.origin}${trackUrl}`).catch(() => {});
      navigate(trackUrl);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Booking failed', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-10">
      <div className="max-w-3xl mx-auto px-4 space-y-5" data-testid="multi-drop-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Package className="h-6 w-6 text-pink-600" /> Multi-Drop Delivery</h1>
          <p className="text-sm text-gray-500">Send multiple packages in one go. One driver, many drops, one fare.</p>
        </div>

        <Card className="p-4 space-y-3">
          <h2 className="font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-pink-600" /> Pickup</h2>
          <div>
            <Label>Pickup location</Label>
            <Input value={pickup} onChange={e => setPickup(e.target.value)} placeholder="Kasama Town Centre" data-testid="multi-drop-pickup" />
          </div>
          <div>
            <Label>Pickup contact (optional)</Label>
            <Input value={pickupContact} onChange={e => setPickupContact(e.target.value)} placeholder="+260 9XX XXX XXX" />
          </div>
        </Card>

        {stops.map((s, idx) => (
          <Card key={idx} className="p-4 space-y-3" data-testid={`multi-drop-stop-${idx}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Drop {idx + 1}</h3>
              {stops.length > 1 && (
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeStop(idx)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Receiver name</Label>
                <Input value={s.receiverName} onChange={e => updateStop(idx, 'receiverName', e.target.value)} data-testid={`multi-drop-name-${idx}`} />
              </div>
              <div>
                <Label>Receiver phone</Label>
                <Input value={s.receiverPhone} onChange={e => updateStop(idx, 'receiverPhone', e.target.value)} placeholder="+260 9XX XXX XXX" />
              </div>
            </div>
            <div>
              <Label>Drop-off location</Label>
              <Input value={s.location} onChange={e => updateStop(idx, 'location', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Package size</Label>
                <div className="flex gap-1 mt-1">
                  {SIZES.map(sz => (
                    <button
                      key={sz.id} type="button"
                      onClick={() => updateStop(idx, 'packageSize', sz.id)}
                      className={`flex-1 px-2 py-1.5 rounded text-xs font-medium border ${s.packageSize === sz.id ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-700 border-gray-300'}`}
                    >{sz.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>COD amount (K, 0 = pre-paid)</Label>
                <Input type="number" step="0.5" min="0" value={s.codAmount} onChange={e => updateStop(idx, 'codAmount', e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Description / notes (optional)</Label>
              <Input value={s.packageDescription} onChange={e => updateStop(idx, 'packageDescription', e.target.value)} placeholder="Documents, fragile electronics, etc." />
            </div>
          </Card>
        ))}

        <Button variant="outline" onClick={addStop} className="w-full" data-testid="multi-drop-add-stop">
          <Plus className="h-4 w-4 mr-1" /> Add another stop
        </Button>

        <Card className="p-4 bg-pink-50 border-pink-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Estimated fare</p>
              <p className="text-2xl font-bold text-pink-600">K{estimate}</p>
              {parseFloat(codTotal) > 0 && <p className="text-xs text-gray-500 mt-1">+ K{codTotal} cash to collect from receivers</p>}
            </div>
            <Phone className="h-8 w-8 text-pink-300" />
          </div>
        </Card>

        <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-pink-600 hover:bg-pink-700" data-testid="multi-drop-submit">
          {submitting ? 'Booking...' : 'Book Multi-Drop Delivery'}
        </Button>
      </div>
    </div>
  );
};

export default BookMultiDrop;
